import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Edit2, Check, X, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

interface EditableDescriptionProps {
  description: string | null | undefined;
  onSave: (newDescription: string) => Promise<void>;
  placeholder?: string;
  className?: string; // For the wrapper div
  textClassName?: string; // For the displayed paragraph
  textareaClassName?: string; // For the Textarea element itself
  rows?: number;
  disabled?: boolean;
  isLoading?: boolean; // External loading state
}

const EditableDescription = forwardRef<HTMLTextAreaElement, EditableDescriptionProps>((
  { 
    description,
    onSave, 
    placeholder = "Enter description...",
    className = '',
    textClassName = 'text-sm text-muted-foreground whitespace-pre-wrap', // Allow wrapping for displayed text
    textareaClassName = 'text-sm',
    rows = 3,
    disabled = false,
    isLoading: externalIsLoading = false,
  },
  ref
) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(description || '');
  const [isSaving, setIsSaving] = useState(false);
  const internalTextareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    setEditValue(description || '');
  }, [description]);

  useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => {
        if (internalTextareaRef.current) {
          internalTextareaRef.current.focus();
          internalTextareaRef.current.select();
        }
      });
    }
  }, [isEditing]);

  const handleStartEdit = (e?: React.MouseEvent | React.KeyboardEvent) => {
    if (e) e.stopPropagation();
    if (disabled || externalIsLoading) return;
    setIsEditing(true);
    setEditValue(description || ''); 
  };

  const handleSave = async () => {
    // Allow saving an empty description if it was initially something else, 
    // or if it was initially empty and user still submits empty (effectively no change or clearing).
    if (editValue.trim() === (description || '').trim()) {
      handleCancel();
      return;
    }

    setIsSaving(true);
    try {
      await onSave(editValue.trim());
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving description:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(description || '');
  };

  // No Enter to save for textarea, only Escape to cancel
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };
  
  const actualIsSaving = isSaving || externalIsLoading;

  if (isEditing) {
    return (
      <div className={cn("flex flex-col items-start gap-2 w-full", className)}>
        <Textarea
          ref={ref ? ref : internalTextareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className={cn("w-full", textareaClassName)}
          disabled={actualIsSaving}
          placeholder={placeholder}
          rows={rows}
          autoFocus
          onFocus={(e) => e.target.select()}
        />
        <div className="flex items-center gap-2 self-end">
          <Button 
            size="sm" 
            onClick={handleSave} 
            disabled={actualIsSaving} 
            className="h-8 px-3"
            aria-label={actualIsSaving ? "Saving description" : "Save description"}
          >
            {actualIsSaving ? <Loader2 className="h-4 w-4 animate-spin" data-testid="loader-icon" /> : <Check className="h-4 w-4" data-testid="check-icon" />} <span className="ml-1">Save</span>
          </Button>
          <Button 
            size="sm" 
            variant="ghost" 
            onClick={handleCancel} 
            disabled={actualIsSaving} 
            className="h-8 px-3"
            aria-label="Cancel edit description"
          >
            <X className="h-4 w-4" data-testid="cancel-icon" /> <span className="ml-1">Cancel</span>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={cn("group min-w-0 relative", className, {"cursor-text": !disabled})}
      onClick={!disabled ? handleStartEdit : undefined} 
      onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) handleStartEdit(e);}}
      role={!disabled ? "button" : undefined}
      tabIndex={!disabled ? 0 : undefined}
      title={!disabled ? "Edit description" : description || undefined}
    >
      <p className={cn({
          'italic text-muted-foreground/70': !description?.trim() && placeholder === "Add a description...",
          'pr-6': !disabled && !actualIsSaving
        }, textClassName)}
      >
        {description?.trim() || placeholder}
      </p>
      {!disabled && !actualIsSaving && !isEditing && (
        <Edit2 className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity absolute right-0 top-0.5" data-testid="edit-icon" />
      )}
      {actualIsSaving && !isEditing && (
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground absolute right-0 top-0.5" data-testid="display-loader-icon" />
      )}
    </div>
  );
});

EditableDescription.displayName = 'EditableDescription';
export default EditableDescription; 